import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { FormInstance } from 'antd';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Popconfirm, Select, Skeleton, Space,
  Statistic, Table, Tag, Typography, message,
} from 'antd';
import { MoneyField, StudentPickerField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type {
  ConcessionAppliesTo, ConcessionRow, ConcessionType,
} from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin — Concessions (M-04). Applied at invoice-generation time only.
 */
const TYPE_LABELS: Record<ConcessionType, string> = {
  sibling: 'Sibling', staff_ward: 'Staff ward', scholarship: 'Scholarship', other: 'Other',
};
const TYPE_TONE: Record<ConcessionType, string> = {
  sibling: 'processing', staff_ward: 'purple', scholarship: 'success', other: 'default',
};

export function ConcessionsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['concessions', institution],
    queryFn: () => productApi.listConcessions(institution),
    enabled: !!institution,
  });
  const del = useMutation({
    mutationFn: (id: string) => productApi.deleteConcession(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['concessions', institution] }); void message.success('Deactivated'); },
  });

  const rows = list.data?.rows ?? [];
  const by = list.data?.by_type ?? { sibling: 0, staff_ward: 0, scholarship: 0, other: 0 };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Concessions</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Sibling / staff-ward / scholarship discounts. Applied at invoice-generation time only (M-04). Existing invoices are not touched.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add concession</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Sibling" value={by.sibling} /></Card>
        <Card size="small"><Statistic title="Staff ward" value={by.staff_ward} valueStyle={{ color: '#553a9c' }} /></Card>
        <Card size="small"><Statistic title="Scholarship" value={by.scholarship} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Other" value={by.other} /></Card>
      </div>

      <Card size="small" title={`${rows.length} concessions`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No concessions. <a onClick={() => setAddOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<ConcessionRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number}</Typography.Text></> },
              { title: 'Type', dataIndex: 'type', key: 't', width: 130, render: (v: ConcessionType) => <Tag color={TYPE_TONE[v]}>{TYPE_LABELS[v]}</Tag> },
              { title: 'Applies to', key: 'at', width: 160, render: (_, r) => r.applies_to === 'all' ? <Tag>all heads</Tag> : <Tag color="processing">{r.fee_head_name}</Tag> },
              {
                title: 'Discount', key: 'd', width: 130, align: 'right',
                render: (_, r) => r.percent_bps > 0 ? <b>{(r.percent_bps / 100).toFixed(r.percent_bps % 100 === 0 ? 0 : 2)}%</b> : <b>₹{paise(r.amount_paise)}</b>,
              },
              { title: 'Reason', dataIndex: 'reason', key: 'r', ellipsis: true },
              { title: 'Status', dataIndex: 'active', key: 'a', width: 100, render: (v: boolean) => v ? <Tag color="success">active</Tag> : <Tag>inactive</Tag> },
              {
                key: 'act', width: 120, align: 'right',
                render: (_, r) => r.active ? (
                  <Popconfirm title="Deactivate this concession?" onConfirm={() => del.mutate(r.id)} okText="Deactivate" okButtonProps={{ danger: true }}>
                    <a>Deactivate</a>
                  </Popconfirm>
                ) : null,
              },
            ]}
          />
        )}
      </Card>

      <AddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['concessions', institution] });
          void message.success('Concession added');
        }}
      />
    </Space>
  );
}

type ConcessionFormValues = {
  student_adm: string; type: ConcessionType; applies_to: ConcessionAppliesTo;
  fee_head_id?: string; percent?: number; amount_rupees?: number; reason?: string;
};

function AddModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const students = useQuery({
    queryKey: ['students-resolve', institution],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution && open,
  });
  const heads = useQuery({
    queryKey: ['fee-heads', institution],
    queryFn: () => productApi.listFeeHeads(institution),
    enabled: !!institution && open,
  });

  return (
    <FormModal<ConcessionFormValues>
      open={open} onClose={onClose}
      title="Add concession" submitText="Add"
      initial={{ type: 'sibling', applies_to: 'all' }}
      mutation={(v) => {
        const student = (students.data?.rows ?? []).find((s) => s.admission_number === v.student_adm);
        if (!student) throw new Error('Pick a student');
        return productApi.createConcession(institution, {
          student_id: student.id,
          type: v.type,
          applies_to: v.applies_to,
          fee_head_id: v.applies_to === 'head' ? v.fee_head_id : undefined,
          percent_bps: v.percent ? Math.round(v.percent * 100) : 0,
          amount_paise: v.amount_rupees ? Math.round(v.amount_rupees * 100) : 0,
          reason: v.reason?.trim() || undefined,
        });
      }}
      onSuccess={onCreated}
    >
      {(vform) => (
        <ConcessionBody
          form={vform.form}
          institution={institution}
          headsOptions={(heads.data?.rows ?? []).map((h) => ({ value: h.id, label: `${h.name} · ₹${paise(h.amount_paise)}` }))}
        />
      )}
    </FormModal>
  );
}

function ConcessionBody({ form, institution, headsOptions }: {
  form: FormInstance<ConcessionFormValues>;
  institution: string;
  headsOptions: { value: string; label: string }[];
}) {
  const appliesTo = Form.useWatch('applies_to', form);
  return (
    <>
      <Field name="student_adm" label="Student" required>
        <StudentPickerField institution={institution} />
      </Field>
      <FormSection cols={2}>
        <Field name="type" label="Type" required>
          <Select options={(Object.keys(TYPE_LABELS) as ConcessionType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] }))} />
        </Field>
        <Field name="applies_to" label="Applies to" required>
          <Select options={[{ value: 'all', label: 'All fee heads' }, { value: 'head', label: 'One head' }]} />
        </Field>
      </FormSection>
      {appliesTo === 'head' && (
        <Field name="fee_head_id" label="Fee head" required>
          <Select options={headsOptions} />
        </Field>
      )}
      <Alert type="info" showIcon message="Choose percent OR amount (not both). Percent applies to the head's amount." style={{ marginBottom: 12 }} />
      <FormSection cols={2}>
        <Field
          name="percent"
          label="Percent"
          dependencies={['amount_rupees']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const amount = getFieldValue('amount_rupees');
                if (value && amount) return Promise.reject(new Error('Set percent OR amount, not both'));
                if (!value && !amount) return Promise.reject(new Error('Set percent or amount'));
                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber min={0} max={100} style={{ width: '100%' }} step={5} suffix="%" />
        </Field>
        <Field name="amount_rupees" label="Fixed amount" dependencies={['percent']}>
          <MoneyField />
        </Field>
      </FormSection>
      <Field name="reason" label="Reason">
        <Input.TextArea rows={2} placeholder="Merit rank / staff ward / etc." />
      </Field>
    </>
  );
}

function paise(n: number): string { return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
